import { useHistory } from "react-router-dom";

// FontAwesome
import {
    faBook,
    faBox,
    faCalendar,
    faCog,
    faDatabase,
    faPaintBrush,
    faPieChart,
    faQuestionCircle,
    faBuilding,
    faLineChart,
    faRocket,
    faChartSimple,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components
import AnalyticsQueue from "./AnalyticsQueue/AnalyticsQueue";
import Logout from "./Logout/Logout";
import MenuItem from "./MenuItem/MenuItem";
import MenuLogo from "./MenuLogo/MenuLogo";
import NotificationsList from "./NotificationsList/NotificationsList";
import ProfileButton from "./ProfileButton/ProfileButton";

// Utils
import { useSettings } from "context/Settings.context";
import { hideBasedOnRoutes } from "utils/common.utils";

// Styles
import { MainMenuContainer } from "./MainMenu.styles";

export default function MainMenu() {
    const history = useHistory();

    const hide = hideBasedOnRoutes(["/home", "/portfolio-live"]);

    const {
        route,
        theme,
        setTheme,
    }: {
        route: string;
        setShowGlobalSearchBox: Function;
        theme: "light" | "dark" | "highContrast";
        setTheme: Function;
    } = useSettings();

    const themesList = {
        dark: "highContrast",
        highContrast: "light",
        light: "dark",
    };

    return (
        <MainMenuContainer>
            <Logout />
            <div className="menuList">
                <MenuLogo />
                {!hide && (
                    <>
                        <MenuItem
                            id={"portfolioMainMenuItem"}
                            text={"PORTFOLIO"}
                            isActive={route.startsWith("/portfolio")}
                            icon={
                                <FontAwesomeIcon icon={faPieChart} fixedWidth />
                            }
                            click={() => history.push("/portfolio/snapshot")}
                            permissions={[
                                "p_bqmq_can_host",
                                "p_bqmq_can_view_results",
                                "p_bqmq_can_vote",
                            ]}
                            menuPage="portfolio"
                        />
                        <MenuItem
                            id={"companyMainMenuItem"}
                            text={"COMPANY"}
                            isActive={route.startsWith("/company")}
                            icon={
                                <FontAwesomeIcon icon={faBuilding} fixedWidth />
                            }
                            click={() =>
                                history.push("/company/company-summary")
                            }
                            permissions={[
                                "P_bqmq_can_host",
                                "p_bqmq_can_view_results",
                                "p_bqmq_can_vote",
                            ]}
                            menuPage="company"
                        />
                        <MenuItem
                            id={"dataMainMenuItem"}
                            isActive={route.startsWith("/data")}
                            text={"DATA"}
                            icon={
                                <FontAwesomeIcon icon={faDatabase} fixedWidth />
                            }
                            click={() => history.push("/data")}
                            permissions={["p_list_extractions"]}
                            menuPage="data"
                        />

                        <MenuItem
                            id={"feesMainMenuItem"}
                            text={"FEES"}
                            isActive={route.startsWith("/fees")}
                            icon={<FontAwesomeIcon icon={faBox} fixedWidth />}
                            click={() =>
                                history.push("/fees/dublin?tab=global")
                            }
                            permissions={[
                                "p_can_view_fees",
                                "p_can_action_fees",
                            ]}
                            menuPage="fees"
                        />

                        <MenuItem
                            id={"dailyRecsMainMenuItem"}
                            text={"DAILY RECS"}
                            isActive={route.startsWith("/reconciliation")}
                            icon={
                                <FontAwesomeIcon icon={faCalendar} fixedWidth />
                            }
                            click={() =>
                                history.push(
                                    "/reconciliation/inputs/incoming_files"
                                )
                            }
                            permissions={[
                                "p_can_view_navs",
                                "p_can_action_navs",
                            ]}
                            menuPage="dailyrecs"
                        />

                        <MenuItem
                            id={"growthVotingMainMenu"}
                            text={"GROWTH V."}
                            isActive={route.startsWith("/growth-voting")}
                            icon={
                                <FontAwesomeIcon
                                    icon={faLineChart}
                                    fixedWidth
                                />
                            }
                            click={() => history.push("/growth-voting")}
                            permissions={["p_can_view_growth_voting"]}
                            menuPage="growthVoting"
                        />

                        <MenuItem
                            id={"missionControlMainMenuItem"}
                            text={"MISSION CONTROL"}
                            isActive={route.startsWith("/mission-control")}
                            icon={
                                <FontAwesomeIcon icon={faRocket} fixedWidth />
                            }
                            click={() =>
                                history.push("/mission-control/fund-metrics")
                            }
                            permissions={[
                                "p_can_view_mission_control",
                                "p_can_view_mission_control_public",
                            ]}
                            menuPage="missionControl"
                        />

                        <MenuItem
                            id={"reportingMainMenuItem"}
                            text="REPORTING"
                            isActive={route.startsWith("/reporting")}
                            icon={
                                <FontAwesomeIcon
                                    icon={faChartSimple}
                                    fixedWidth
                                />
                            }
                            click={() =>
                                history.push("/reporting/thirty-day-summary")
                            }
                            permissions={["p_can_view_reporting"]}
                            menuPage="reporting"
                        />

                        <MenuItem
                            id={"bqmqMainMenuItem"}
                            text={"HELP"}
                            isActive={route.startsWith("/help")}
                            icon={
                                <FontAwesomeIcon
                                    icon={faQuestionCircle}
                                    fixedWidth
                                />
                            }
                            click={() =>
                                history.push(
                                    "/help/documentation/help/Home.help.mdx"
                                )
                            }
                            permissions={["p_can_view_help"]}
                            menuPage="help"
                        />
                    </>
                )}
            </div>

            <div className="menuList">
                {!hide && (
                    <>
                        <MenuItem
                            id={"bqmqMainMenuItem"}
                            text={"DOCS"}
                            isActive={route.startsWith("/docs")}
                            icon={<FontAwesomeIcon icon={faBook} fixedWidth />}
                            click={() =>
                                history.push(
                                    "/docs/documentation/dev/Home.doc.mdx"
                                )
                            }
                            permissions={["p_can_view_docs"]}
                            menuPage="docs"
                        />

                        <AnalyticsQueue />
                        <NotificationsList />
                    </>
                )}
                <MenuItem
                    isActive={false}
                    text={"CHANGE THEME"}
                    icon={<FontAwesomeIcon icon={faPaintBrush} fixedWidth />}
                    click={() => setTheme(themesList[theme])}
                    permissions={["p_theme_can_toggle"]}
                />
                <MenuItem
                    id={"adminMainMenuBtn"}
                    text={"ADMIN"}
                    isActive={route.startsWith("/admin")}
                    icon={<FontAwesomeIcon icon={faCog} fixedWidth />}
                    click={() => history.push("/admin/permissions")}
                    permissions={["s_update_permissions"]}
                    menuPage="admin"
                />

                <ProfileButton />
            </div>
        </MainMenuContainer>
    );
}
